// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// The one earth model for every distance the app quotes.
//
// Before this module existed the same measurement was computed against four different
// earth constants in as many files, so the course editor, the round view and the guide
// arcs could each print a slightly different number for the same ground. Every
// great-circle distance and every local-plane conversion now derives from the single
// radius below; a new feature that needs one imports from here rather than declaring
// its own.
//
// Pure module, no platform and no feature imports, so both services/ and features/
// can depend on it.

type LatLon = { lat: number; lon: number };

/** IUGG mean earth radius in metres — matches the wear app's haversine. */
export const EARTH_RADIUS_M = 6371008.8;

/** Ground metres per degree of latitude on the same sphere. */
export const METERS_PER_DEGREE_LAT = (EARTH_RADIUS_M * Math.PI) / 180;

/** Great-circle distance in metres. */
export const distanceMeters = (from: LatLon, to: LatLon): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
};
